import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButton } from '@mui/material';
import dayjs from 'dayjs';
import { Label } from '@components/Label';
import { UsageRequestEditModal } from '@components/products/usageRequest/UsageRequestEditModal';
import { StatusChip } from '@pages/customer/common/statusChip/StatusChip';
import { useStore } from '@stores/RootStore';
import { ChevronIcon } from '@icons/ChevronIcon';
import { PencilIcon } from '@icons/PencilIcon';
import { TrashIcon } from '@icons/TrashIcon';
import { useDeleteSubscriptionUseRequest, useGetSubscriptionUseRequest } from '@queryHooks/useSubscriptionUseRequest';
import { dateFormat } from '@utils/dateUtil';
import * as S from './ApplyDetailStyles';

export const ApplyDetail = () => {
  const { t } = useTranslation();
  const {
    uiStore: { alertStore: deleteModal },
    categoryStore,
  } = useStore();
  const params = useParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data: applySb } = useGetSubscriptionUseRequest(params.id as string);
  const { mutate: deleteApply } = useDeleteSubscriptionUseRequest();

  const [isOpenModifyModal, setIsOpenModifyModal] = useState<boolean>(false);

  const handleDelete = () => {
    deleteApply(
      { requestId: params.id as string },
      {
        onSuccess: () => {
          navigate('/subscriptions/usage-apply');
        },
      },
    );
  };

  const productType = applySb?.categoryId ? categoryStore.findRootCategory(applySb.categoryId)?.name : '';

  return (
    <S.Container>
      <S.Header>
        <IconButton onClick={() => navigate(-1)}>
          <ChevronIcon rotateNum={270} />
        </IconButton>
        <span>{t('Apply_Detail_01')}</span>
      </S.Header>
      <S.Body>
        <S.TitleHeader>
          <span className="title">{t('Apply_Detail_02')}</span>
          {applySb?.status === 'CREATED' || applySb?.status === 'PENDING' ? (
            <div className="edit">
              <S.Button
                variant="outline"
                size="small"
                onClick={() => setIsOpenModifyModal(true)}
                paddingHorizontal={12}
              >
                <PencilIcon width={16} height={16} />
                <span>{t('Ad_Cart_09')}</span>
              </S.Button>
              <S.Button
                variant="outline"
                size="small"
                paddingHorizontal={12}
                onClick={() =>
                  deleteModal.open({
                    confirmName: t('Subscrib_Detail_BuyRequest_12'),
                    message: t('Apply_Detail_03'),
                    title: t('Apply_Detail_04'),
                    useCancel: true,
                    cancelName: t('Subscrib_Detail_BuyRequest_13'),
                    onConfirm: handleDelete,
                    type: 'error',
                  })
                }
              >
                <TrashIcon width={16} height={16} />
                <span>{t('Subscrib_Detail_BuyRequest_12')}</span>
              </S.Button>
            </div>
          ) : null}
        </S.TitleHeader>
        <S.Frame>
          <S.Columns>
            <S.Column divide>
              <span className="content-name">{t('Subscrib_Detail_UseRequest_05')}</span>
              <span className="content-value">{dayjs(applySb?.createdDate).format(t('DateFormat_YMD'))}</span>
            </S.Column>
            <S.Column divide={false}>
              <span className="content-name">{t('Subscrib_Detail_UseRequest_06')}</span>
              <span className="content-value">{productType}</span>
            </S.Column>
          </S.Columns>
          <S.Columns>
            <S.Column divide>
              <span className="content-name">{t('Subscrib_Detail_UseRequest_07')}</span>
              <span className="content-value">{applySb?.productName}</span>
            </S.Column>
            <S.Column divide>
              <span className="content-name">{t('Subscrib_Main_12')}</span>
              <span className="content-value">{applySb?.planName}</span>
            </S.Column>
          </S.Columns>
          <S.Columns>
            <S.Column divide>
              <span className="content-name">{t('Subscrib_Detail_UseRequest_35')}</span>
              <span className="content-value">{applySb?.subscription?.name ?? '-'}</span>
            </S.Column>
            <S.Column divide>
              <span className="content-name">{t('Acc_Detail_04')}</span>
              <span className="content-value">{`${dateFormat(
                applySb?.startDate,
                '-',
                t('DateFormat_YMD'),
              )} ~ ${dateFormat(applySb?.endDate, '-', t('DateFormat_YMD'))}`}</span>
            </S.Column>
          </S.Columns>
          <S.Columns>
            <S.Column divide>
              <span className="content-name">{t('Subscrib_Detail_UseRequest_15')}</span>
              <span className="inquiry">{applySb?.inquiry.replaceAll(' ', '') ? applySb?.inquiry : '-'}</span>
            </S.Column>
          </S.Columns>
        </S.Frame>

        <S.TitleHeader>
          <span className="title">
            {applySb?.approval ? t('Approval_Detail_Process_History') : t('Request_Processing')}
          </span>
        </S.TitleHeader>
        {!applySb?.approval &&
          applySb?.histories?.map(history => {
            return (
              <S.Frame key={history.status}>
                <S.Columns>
                  <S.Column divide>
                    <span className="content-name">{t('Subscrib_Detail_UseRequest_09')}</span>
                    <span className="content-value">
                      <Label variant="contain" color={history.statusLabelColor}>
                        {history.statusStr}
                      </Label>
                    </span>
                  </S.Column>
                  <S.Column divide={false}>
                    <span className="content-name">{t('Subscrib_Detail_UseRequest_33')}</span>
                    <span className="content-value">{dayjs(history.createdDate).format(t('DateFormat_YMD'))}</span>
                  </S.Column>
                </S.Columns>
                <S.Columns>
                  <S.Column divide>
                    <span className="content-name">{t('Apply_Detail_05')}</span>
                    <span className="content-value">{history.managerName || '-'}</span>
                  </S.Column>
                  <S.Column divide={false} />
                </S.Columns>
                <S.Columns>
                  <S.Column divide={false}>
                    <span className="content-name">{t('Subscrib_Detail_UseRequest_15')}</span>
                    <span className="inquiry">{history.comment || '-'}</span>
                  </S.Column>
                </S.Columns>
              </S.Frame>
            );
          })}
        {applySb?.approval && (
          <S.Frame>
            <S.Columns key={0}>
              <S.Column divide={false}>
                <ApprovalStepContainer>
                  <ApprovalStepNumber>{0}</ApprovalStepNumber>
                  <ApprovalStepContent>
                    <ApproverInfo>{applySb.approval.drafterName}</ApproverInfo>
                    <ApprovalStepStatusContainer>
                      <StatusChip
                        status={t('Approval_Status_Draft')}
                        backgroundColor={theme.colors['bg-gray-dark']}
                        color={theme.colors['text-white']}
                      />
                      <ApprovalStepUpdatedAt>{applySb.approval.createdDate}</ApprovalStepUpdatedAt>
                    </ApprovalStepStatusContainer>
                  </ApprovalStepContent>
                </ApprovalStepContainer>
              </S.Column>
            </S.Columns>
            {applySb.approval.steps
              .sort((a, b) => a.stepOrder - b.stepOrder)
              .map(step => (
                <S.Columns key={step.id}>
                  <S.Column divide={false}>
                    <ApprovalStepContainer>
                      <ApprovalStepNumber>{step.stepOrder + 1}</ApprovalStepNumber>
                      <ApprovalStepContent>
                        <ApproverInfo> {step.approverInfoLabel}</ApproverInfo>
                        {step.status !== 'WAITING' && (
                          <>
                            <ApprovalStepStatusContainer>
                              <StatusChip
                                status={step.statusLabel}
                                backgroundColor={
                                  theme.colors[step.statusBackgroundColor as keyof typeof theme.colors] as string
                                }
                                color={theme.colors[step.statusTextColor as keyof typeof theme.colors] as string}
                              />
                              <ApprovalStepUpdatedAt>{step.approvedAt ?? '-'}</ApprovalStepUpdatedAt>
                            </ApprovalStepStatusContainer>
                            <ApprovalStepComment>{step.comment ?? '-'}</ApprovalStepComment>
                          </>
                        )}
                      </ApprovalStepContent>
                    </ApprovalStepContainer>
                  </S.Column>
                </S.Columns>
              ))}
          </S.Frame>
        )}
      </S.Body>
      <UsageRequestEditModal
        isOpen={isOpenModifyModal}
        onClose={() => setIsOpenModifyModal(false)}
        swId={applySb?.productId as string}
        modifyData={applySb}
      />
    </S.Container>
  );
};

const ApprovalStepContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  padding: 15px 20px;
  border-bottom: 1px solid ${({ theme: { colors } }) => colors['border-gray-lighter']};
  background-color: ${({ theme: { colors } }) => colors['bg-white']};

  &:last-child {
    border-bottom: none;
    border-radius: 0 0 5px 5px;
  }

  &:first-of-type {
    border-radius: 5px 5px 0 0;
  }
`;

const ApprovalStepNumber = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 2px;
  background-color: ${({ theme: { colors } }) => colors['bg-gray-light']};
  color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.54;
  letter-spacing: 0.015em;
  flex-shrink: 0;
`;

const ApprovalStepContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
`;

const ApproverInfo = styled.div`
  font-family: 'Pretendard';
  font-size: 14px;
  font-weight: 700;
  line-height: 1.43;
  letter-spacing: 0.011em;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
`;

const ApprovalStepStatusContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const ApprovalStepUpdatedAt = styled.span`
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 700;
  line-height: 1.54;
  letter-spacing: 0.012em;
  color: ${({ theme: { colors } }) => colors['text-gray-sub-darker']};
  width: 116px;
`;

const ApprovalStepComment = styled.div`
  font-family: 'Pretendard';
  font-size: 13px;
  font-weight: 500;
  line-height: 1.54;
  letter-spacing: 0.012em;
  color: ${({ theme: { colors } }) => colors['text-gray-main']};
  word-break: break-word;
`;
